/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Model, { attr } from '@ember-data/model';
import type { Block } from '@intercom/interblocks.ts';
import { type Status } from '../data/content-service/content-review-statuses';
import { type EntityType } from '../data/entity-types';
import { humanReadableObjectNames } from '../data/matching-system/matching-constants';

export enum ReviewType {
  CREATE_CONTENT = 'create_content',
  EDIT_CONTENT = 'edit_content',
  DELETE_CONTENT = 'delete_content',
  DUPLICATE_CONTENT = 'duplicate_content',
  CONTRADICTING_CONTENT = 'contradicting_content',
  UNDERPERFORMING_CONTENT = 'underperforming_content',
}

export enum BlockEditType {
  EDIT_EXISTING = 'edit_existing',
  APPEND_NEW_BLOCKS = 'append_new_blocks',
}

export type Suggestion = {
  indices: [number, number];
  editedBlocks: Block[];
  includedConversationIds: number[];
  unresolvedConversationIds: number[];
  blockEditType: BlockEditType;
  topic: string;
  seedContentExplanation?: string;
  numberOfInvolvedConversations: number;
  numberOfResolvedConversations: number;
  resolutionRate: number;
  appResolutionRate: number;
  resolutionRateDifference: number;
};

export default class ContentReviewRequest extends Model {
  @attr('number') declare reviewerId: number;
  @attr('number') declare status: Status;
  @attr('number') declare entityId: number;
  @attr('number') declare entityType: number;
  @attr('date') declare createdAt: Date;
  @attr('date') declare updatedAt: Date;
  @attr('string') declare reviewReason: string;
  @attr('string') declare reviewType: ReviewType;
  @attr() declare updatedSuggestionBlocks: Array<Block>;
  @attr('string') declare title?: string | null;
  @attr() declare suggestion?: Suggestion | null;
  @attr('number') declare approveAsEntityType?: EntityType | null;

  get isContradictingContent() {
    return this.reviewType === ReviewType.CONTRADICTING_CONTENT;
  }

  get isDuplicateContent() {
    return this.reviewType === ReviewType.DUPLICATE_CONTENT;
  }

  get isCreateContent() {
    return this.reviewType === ReviewType.CREATE_CONTENT;
  }

  get isEditContent() {
    return this.reviewType === ReviewType.EDIT_CONTENT;
  }

  get isUnderperformingContent() {
    return this.reviewType === ReviewType.UNDERPERFORMING_CONTENT;
  }

  get contentTypeHumanReadableName() {
    return humanReadableObjectNames[this.entityType]?.toLowerCase();
  }
}
